import { FlightLookupError } from "../types";

const MIN_PREFIX_LENGTH = 2;
const MAX_PREFIX_LENGTH = 3;
const MIN_DIGITS = 1;
const MAX_DIGITS = 4;

const DAYS_BACK = 90;
const DAYS_FORWARD = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

const normalizeFlightNumber = (raw: string): string =>
  raw.replace(/\s+/g, "").toUpperCase();

/**
 * Two or three alphanumeric characters, then one to four digits, with an
 * optional trailing letter (R-A2 EC1). Deliberately not a single regex:
 * a naive `^[A-Z0-9]{2,3}\d{1,4}[A-Z]?$` is ambiguous on inputs such as
 * "CX54999" (2-letter code + 5 digits) because its greedy alphanumeric
 * prefix can "steal" a digit, matching as "CX5" + "4999" and letting an
 * over-long number pass. Taking the maximal trailing run of digits
 * removes that ambiguity while still allowing alphanumeric prefixes
 * (e.g. "5J123").
 */
const isValidFlightNumberFormat = (normalized: string): boolean => {
  let body = normalized;

  if (/^[A-Z]$/.test(body.slice(-1))) {
    body = body.slice(0, -1);
  }

  const digitsMatch = body.match(/\d+$/);
  const digits = digitsMatch ? digitsMatch[0] : "";
  const prefix = digits ? body.slice(0, body.length - digits.length) : body;

  return (
    digits.length >= MIN_DIGITS &&
    digits.length <= MAX_DIGITS &&
    prefix.length >= MIN_PREFIX_LENGTH &&
    prefix.length <= MAX_PREFIX_LENGTH
  );
};

const isWithinDateWindow = (isoDate: string, todayIso: string): boolean => {
  const target = new Date(`${isoDate}T00:00:00Z`).getTime();
  const today = new Date(`${todayIso}T00:00:00Z`).getTime();
  const diffDays = Math.round((target - today) / MS_PER_DAY);
  return diffDays >= -DAYS_BACK && diffDays <= DAYS_FORWARD;
};

export interface ValidationResult {
  valid: boolean;
  error?: FlightLookupError["type"];
}

export const validateFlightQuery = (
  flightNumberRaw: string,
  isoDate: string,
  todayIso: string = new Date().toISOString().slice(0, 10),
): ValidationResult => {
  const normalized = normalizeFlightNumber(flightNumberRaw);
  if (!isValidFlightNumberFormat(normalized)) {
    return { valid: false, error: "malformed" };
  }
  if (!isWithinDateWindow(isoDate, todayIso)) {
    return { valid: false, error: "outOfRange" };
  }
  return { valid: true };
};
